import { useEffect, useState } from 'react';
import { Environment, Network, RecordSource, Store } from 'relay-runtime';

import { composeMiddlewares } from '../compose-middlewares';
import { getAvailableServices } from '../get-available-services';
import { EnvironmentConfigType, MiddlewareCreatorType } from '../relay-types';
import { tokenRefresher } from '../token-refresher';

export const useEnvironment = (config: EnvironmentConfigType, middlewareCreators: MiddlewareCreatorType[]) => {
    const [relayEnvironment, setEnvironment] = useState<Environment | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let isCancelled = false;

        (async () => {
            try {
                setIsLoading(true);
                setError(null);

                const refreshedToken = config.devToken ?? (await tokenRefresher(config));
                const middlewareTokenRefresher = () => tokenRefresher(config);

                const availableServices = await getAvailableServices(config, refreshedToken);
                const middlewares = middlewareCreators.map((creator) =>
                    creator(availableServices, middlewareTokenRefresher, config.devToken),
                );

                const relayFetch = composeMiddlewares(middlewares);

                const relayEnvironment = new Environment({
                    network: Network.create(relayFetch),
                    store: new Store(new RecordSource()),
                });

                if (!isCancelled) {
                    setEnvironment(relayEnvironment);
                }
            } catch (err) {
                if (!isCancelled) {
                    setError(err instanceof Error ? err : new Error('Unknown error'));
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [config, middlewareCreators]);

    return {
        relayEnvironment,
        isLoading,
        error,
    };
};
